import { z } from 'zod';
import { toDocumentId, toModelId, toRevisionId } from '@/shared/types/common.types';
import { tolerantEnum, v1DateSchema } from '@/shared/utils/api/serverPrimitives';
import { cursorPageSchema } from '@/features/workspace/api/workspaceContracts';
import { AVAILABILITIES, CLASSIFICATIONS, DOCUMENT_TYPES, REVISION_STATUSES } from '../types/document.types';
import type {
  ContentUrl,
  DocumentDetail,
  DocumentFacets,
  DocumentRevision,
  DocumentSummary,
  RevisionRef,
  SheetRow,
} from '../types/document.types';

/**
 * The wire contract for `/api/v1/projects/{projectId}/documents`, from Appendix A of the
 * plan in Conbent's `docs/features/project-documents-implementation.md`.
 *
 * The list returns the document object without `description`, `createdAt`, `createdBy`,
 * `etag`, `sheet` and `permissions`; the detail adds them.
 */
const personRefSchema = z.object({ subject: z.string(), displayName: z.string() });

const revisionRefSchema = z.object({
  id: z.string().uuid(),
  versionNumber: z.number().int().positive(),
  revisionCode: z.string(),
  status: tolerantEnum(REVISION_STATUSES),
  availability: tolerantEnum(AVAILABILITIES),
});

export const documentSummarySchema = z.object({
  id: z.string().uuid(),
  documentType: tolerantEnum(DOCUMENT_TYPES),
  classification: tolerantEnum(CLASSIFICATIONS),
  title: z.string(),
  documentNumber: z.string().nullable(),
  disciplineCode: z.string().nullable(),
  currentRevision: revisionRefSchema.nullable(),
  isDeleted: z.boolean(),
  updatedAt: v1DateSchema,
  updatedBy: personRefSchema,
});

export const documentDetailSchema = documentSummarySchema.extend({
  description: z.string().nullable(),
  sheet: z.object({ sheetName: z.string(), modelId: z.number().int().nullable() }).nullable(),
  createdAt: v1DateSchema,
  createdBy: personRefSchema,
  etag: z.string().min(1),
  permissions: z.object({
    canEdit: z.boolean(),
    canUploadRevision: z.boolean(),
    canChangeStatus: z.boolean(),
    canDelete: z.boolean(),
  }),
});

export const documentPageSchema = cursorPageSchema(documentSummarySchema);

export const revisionSchema = revisionRefSchema.extend({
  failureCode: z.string().nullable(),
  description: z.string().nullable(),
  fileName: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  contentType: z.string().nullable(),
  sha256: z.string().nullable(),
  createdAt: v1DateSchema,
  createdBy: personRefSchema,
});

export const revisionPageSchema = cursorPageSchema(revisionSchema);

export const contentUrlSchema = z.object({ url: z.string().url(), expiresAt: v1DateSchema });

export const sheetRowSchema = z.object({
  documentId: z.string().uuid(),
  sheetNumber: z.string().nullable(),
  sheetName: z.string(),
  disciplineCode: z.string().nullable(),
  modelId: z.number().int().nullable(),
  currentRevision: revisionRefSchema.nullable(),
});

export const sheetPageSchema = cursorPageSchema(sheetRowSchema);

export const documentFacetsSchema = z.object({
  disciplines: z.array(z.object({ code: z.string(), count: z.number().int().nonnegative() })),
  statuses: z.array(
    z.object({ status: tolerantEnum(REVISION_STATUSES), count: z.number().int().nonnegative() })
  ),
});

function toRevisionRef(raw: z.output<typeof revisionRefSchema>): RevisionRef {
  return { ...raw, id: toRevisionId(raw.id) };
}

export function toDocumentSummary(raw: z.output<typeof documentSummarySchema>): DocumentSummary {
  return {
    ...raw,
    id: toDocumentId(raw.id),
    currentRevision: raw.currentRevision === null ? null : toRevisionRef(raw.currentRevision),
  };
}

export function toDocumentDetail(raw: z.output<typeof documentDetailSchema>): DocumentDetail {
  return {
    ...raw,
    ...toDocumentSummary(raw),
    sheet:
      raw.sheet === null
        ? null
        : {
            sheetName: raw.sheet.sheetName,
            modelId: raw.sheet.modelId === null ? null : toModelId(raw.sheet.modelId),
          },
  };
}

export function toDocumentFacets(raw: z.output<typeof documentFacetsSchema>): DocumentFacets {
  return raw;
}

export function toDocumentRevision(raw: z.output<typeof revisionSchema>): DocumentRevision {
  return { ...raw, id: toRevisionId(raw.id) };
}

export function toContentUrl(raw: z.output<typeof contentUrlSchema>): ContentUrl {
  return raw;
}

export function toSheetRow(raw: z.output<typeof sheetRowSchema>): SheetRow {
  return {
    ...raw,
    documentId: toDocumentId(raw.documentId),
    modelId: raw.modelId === null ? null : toModelId(raw.modelId),
    currentRevision: raw.currentRevision === null ? null : toRevisionRef(raw.currentRevision),
  };
}
